import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Send, Loader2, ShieldCheck, Download, CheckCircle2, AlertTriangle, Clock, FileText, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RequestCOIDialog — v2.9.8 · "Request Compliance Docs"
 * Request AND upload BOTH the Certificate of Insurance (COI) and the W-9 for
 * one or many contractors on a project.
 *  - Pick which docs to request (COI, W-9, or both), select contractors, send.
 *  - The COI is project-scoped (expiration-tracked). The W-9 is UNIVERSAL —
 *    good for the calendar year — so a W-9 on file shows everywhere the
 *    contractor appears (project + global W-9 page), regardless of where it
 *    was uploaded.
 *  - Owners can upload EITHER doc on a contractor's behalf ("old school" subs).
 */

// COI status → display badge + validity.
function coiMeta(coi) {
  const s = coi?.status;
  if (s === 'received') return { label: 'On file', valid: true, tone: 'received', icon: CheckCircle2 };
  if (s === 'satisfied_by_global') return { label: 'Valid (global)', valid: true, tone: 'received', icon: CheckCircle2 };
  if (s === 'expired') return { label: 'Expired', valid: false, tone: 'expired', icon: AlertTriangle };
  if (s === 'required') return { label: 'Requested', valid: false, tone: 'requested', icon: Clock };
  return { label: 'Not on file', valid: false, tone: 'missing', icon: FileText };
}

// W-9 status → display badge + validity (calendar-year rule).
function w9Meta(w9) {
  const s = w9?.status;
  if (s === 'received') return { label: 'On file', valid: true, tone: 'received', icon: CheckCircle2 };
  if (s === 'renew') return { label: 'Renew this year', valid: false, tone: 'expired', icon: AlertTriangle };
  return { label: 'Not on file', valid: false, tone: 'missing', icon: FileText };
}

const toneClasses = {
  received: 'bg-green-100 text-green-700 border-green-200',
  expired: 'bg-red-100 text-red-700 border-red-200',
  requested: 'bg-amber-100 text-amber-700 border-amber-200',
  missing: 'bg-slate-100 text-slate-600 border-slate-200',
};

export default function RequestCOIDialog({ open, onOpenChange, projectId, contractors = [] }) {
  const [selected, setSelected] = useState({}); // { [contractorId]: true }
  const [reqCoi, setReqCoi] = useState(true);
  const [reqW9, setReqW9] = useState(false);
  const [additionalInsured, setAdditionalInsured] = useState('');
  const [requiredExpiration, setRequiredExpiration] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [rowByContractor, setRowByContractor] = useState({}); // { [id]: { coi, w9 } }
  const [uploading, setUploading] = useState({}); // { [`${id}:${kind}`]: true }

  // Hidden file inputs keyed per contractor+doc kind so owners can attach docs.
  const fileInputs = useRef({});

  const loadStatuses = useCallback(async () => {
    if (!projectId) return;
    setLoadingStatus(true);
    try {
      const res = await apiService.getProjectCOIs(projectId);
      const map = {};
      (res.data?.cois || []).forEach((row) => {
        map[row.contractor_id] = { coi: row.coi, w9: row.w9 };
      });
      setRowByContractor(map);
    } catch (e) {
      // Non-fatal — the request form still works without statuses.
    } finally {
      setLoadingStatus(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) {
      setSelected({});
      setReqCoi(true);
      setReqW9(false);
      setAdditionalInsured('');
      setRequiredExpiration('');
      loadStatuses();
    }
  }, [open, loadStatuses]);

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const allChecked = contractors.length > 0 && contractors.every((c) => selected[c.id]);

  const toggleSelectAll = () => {
    if (allChecked) {
      setSelected({});
    } else {
      const next = {};
      contractors.forEach((c) => { next[c.id] = true; });
      setSelected(next);
    }
  };

  const submit = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one contractor');
      return;
    }
    const formTypes = [];
    if (reqCoi) formTypes.push('coi');
    if (reqW9) formTypes.push('w9');
    if (formTypes.length === 0) {
      toast.error('Pick at least one document to request (COI or W-9)');
      return;
    }
    setSending(true);
    const results = await Promise.allSettled(
      selectedIds.map((cid) => apiService.createFormRequest({
        project_id: projectId,
        contractor_id: cid,
        form_types: formTypes,
        additional_insured: additionalInsured || null,
        coi_required_expiration: requiredExpiration || null,
      }, window.location.origin)),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - ok;
    setSending(false);
    const label = formTypes.map((f) => (f === 'coi' ? 'COI' : 'W-9')).join(' + ');
    if (ok > 0) {
      toast.success(`${label} request sent to ${ok} contractor${ok > 1 ? 's' : ''}${failed ? ` — ${failed} failed` : ''}`);
    }
    if (ok === 0) {
      toast.error('Failed to send compliance requests');
    }
    setSelected({});
    loadStatuses();
    if (failed === 0) onOpenChange(false);
  };

  const openDownload = (doc) => {
    if (!doc?.download_url) return;
    window.open(`${BACKEND_URL}${doc.download_url}`, '_blank', 'noopener');
  };

  const triggerUpload = (contractorId, kind) => {
    const el = fileInputs.current[`${contractorId}:${kind}`];
    if (el) el.click();
  };

  const handleOwnerUpload = async (contractorId, kind, file) => {
    if (!file) return;
    const key = `${contractorId}:${kind}`;
    if (kind === 'coi') {
      const today = new Date().toISOString().split('T')[0];
      const expIso = window.prompt('COI expiration date is required (YYYY-MM-DD)', today);
      if (!expIso) { toast.error('COI upload cancelled — expiration date is required.'); return; }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(expIso.trim())) { toast.error('Invalid date format. Use YYYY-MM-DD.'); return; }
      setUploading((p) => ({ ...p, [key]: true }));
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('expiration_date', expIso.trim());
        await apiService.uploadContractorCOI(contractorId, fd);
        toast.success('COI uploaded');
        loadStatuses();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to upload COI');
      } finally {
        setUploading((p) => ({ ...p, [key]: false }));
      }
    } else {
      setUploading((p) => ({ ...p, [key]: true }));
      try {
        const fd = new FormData();
        fd.append('file', file);
        await apiService.uploadContractorW9(contractorId, fd);
        toast.success('W-9 uploaded — now on file for every project this year');
        loadStatuses();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to upload W-9');
      } finally {
        setUploading((p) => ({ ...p, [key]: false }));
      }
    }
  };

  const contractorName = (c) => c.company || c.name;

  const UploadBtn = ({ contractorId, kind }) => {
    const key = `${contractorId}:${kind}`;
    const busy = !!uploading[key];
    return (
      <>
        <input
          ref={(el) => { fileInputs.current[key] = el; }}
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/jpg,image/webp,image/heic"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleOwnerUpload(contractorId, kind, f);
            e.target.value = '';
          }}
          data-testid={`compliance-upload-input-${kind}-${contractorId}`}
        />
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 text-[11px] border-blue-300 text-blue-700 hover:bg-blue-50"
          disabled={busy}
          onClick={() => triggerUpload(contractorId, kind)}
          data-testid={`compliance-upload-btn-${kind}-${contractorId}`}
        >
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
          Upload
        </Button>
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" /> Request Compliance Docs
          </DialogTitle>
          <DialogDescription>
            Request or upload the Certificate of Insurance (COI) and W-9 for the contractors on this project. The W-9 is universal — good for the whole calendar year and visible on every project once on file.
          </DialogDescription>
        </DialogHeader>

        {/* ── Request section ─────────────────────────────── */}
        <div className="space-y-4 py-1">
          <div>
            <Label className="text-sm font-semibold text-slate-700">Documents to request</Label>
            <div className="flex items-center gap-5 mt-2">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                <Checkbox
                  checked={reqCoi}
                  onCheckedChange={(v) => setReqCoi(!!v)}
                  data-testid="compliance-doctype-coi"
                />
                Certificate of Insurance (COI)
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                <Checkbox
                  checked={reqW9}
                  onCheckedChange={(v) => setReqW9(!!v)}
                  data-testid="compliance-doctype-w9"
                />
                W-9
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold text-slate-700">Request from contractors</Label>
            {contractors.length > 0 && (
              <button
                type="button"
                onClick={toggleSelectAll}
                data-testid="coi-select-all-toggle"
                className="text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                {allChecked ? 'Clear all' : 'Select all'}
              </button>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-48 overflow-y-auto" data-testid="coi-contractor-list">
            {contractors.length === 0 && (
              <p className="text-sm text-slate-500 px-3 py-4 text-center">No contractors on this project yet.</p>
            )}
            {contractors.map((c) => (
              <label key={c.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50">
                <Checkbox
                  checked={!!selected[c.id]}
                  onCheckedChange={(v) => setSelected((prev) => ({ ...prev, [c.id]: !!v }))}
                  data-testid={`coi-contractor-checkbox-${c.id}`}
                />
                <span className="flex-1 text-sm text-slate-800">{contractorName(c)}</span>
              </label>
            ))}
          </div>

          {reqCoi && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="coi-ai" className="text-xs">Additional insured (COI)</Label>
                <Input
                  id="coi-ai"
                  placeholder="e.g. First National Bank & 123 Main St"
                  value={additionalInsured}
                  onChange={(e) => setAdditionalInsured(e.target.value)}
                  data-testid="coi-additional-insured-input"
                />
              </div>
              <div>
                <Label htmlFor="coi-exp" className="text-xs">Required expiration (COI, optional)</Label>
                <Input
                  id="coi-exp"
                  type="date"
                  value={requiredExpiration}
                  onChange={(e) => setRequiredExpiration(e.target.value)}
                  data-testid="coi-required-expiration-input"
                />
              </div>
            </div>
          )}

          <Button
            onClick={submit}
            disabled={sending || selectedIds.length === 0}
            className="w-full"
            data-testid="coi-send-request-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            {selectedIds.length > 1 ? `Send ${selectedIds.length} requests` : 'Send request'}
          </Button>
        </div>

        <Separator />

        {/* ── Per-project compliance list ─────────────────── */}
        <div className="space-y-2 py-1" data-testid="coi-requests-list">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold text-slate-700">Compliance status for this project</Label>
            {loadingStatus && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          </div>
          {contractors.length === 0 ? (
            <p className="text-sm text-slate-500 py-2">Nothing to show yet.</p>
          ) : (
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
              {contractors.map((c) => {
                const row = rowByContractor[c.id] || {};
                const cMeta = coiMeta(row.coi);
                const wMeta = w9Meta(row.w9);
                return (
                  <div key={c.id} className="px-3 py-2.5 space-y-1.5" data-testid={`coi-status-row-${c.id}`}>
                    <p className="text-sm font-medium text-slate-800 truncate">{contractorName(c)}</p>

                    {/* COI line */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-9 shrink-0">COI</span>
                      <Badge variant="outline" className={`text-[11px] ${toneClasses[cMeta.tone]}`}>{cMeta.label}</Badge>
                      {row.coi?.expiration && (
                        <span className="text-[11px] text-slate-500">exp {new Date(row.coi.expiration).toLocaleDateString()}</span>
                      )}
                      <div className="ml-auto flex items-center gap-1.5">
                        {row.coi?.download_url && (
                          <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openDownload(row.coi)} data-testid={`coi-download-btn-${c.id}`}>
                            <Download className="w-3.5 h-3.5 mr-1" /> View
                          </Button>
                        )}
                        <UploadBtn contractorId={c.id} kind="coi" />
                      </div>
                    </div>

                    {/* W-9 line */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-9 shrink-0">W-9</span>
                      <Badge variant="outline" className={`text-[11px] ${toneClasses[wMeta.tone]}`}>{wMeta.label}</Badge>
                      {row.w9?.received_date && (
                        <span className="text-[11px] text-slate-500">{new Date(row.w9.received_date).getFullYear()}</span>
                      )}
                      <div className="ml-auto flex items-center gap-1.5">
                        {row.w9?.download_url && (
                          <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openDownload(row.w9)} data-testid={`w9-download-btn-${c.id}`}>
                            <Download className="w-3.5 h-3.5 mr-1" /> View
                          </Button>
                        )}
                        <UploadBtn contractorId={c.id} kind="w9" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
